import Alert from "@amzn/meridian/alert";
import Button from "@amzn/meridian/button";
import Card, { CardHeader } from "@amzn/meridian/card";
import Column from "@amzn/meridian/column";
import Heading from "@amzn/meridian/heading";
import Input from "@amzn/meridian/input";
import Modal, { ModalFooter } from "@amzn/meridian/modal";
import Row from "@amzn/meridian/row";
import React, { useEffect, useState } from "react";
import { getStationCode } from "../../network/NetworkUtil";
import AppLoader from "../common/appLoader";
import commonUtils, { filterBosonVariance, getReadableLabel, getRemittanceRequestParams, groupBy, insertVarianceByUniqueReasonCode } from "../common/commonUtils";
import { ErrorCodes, RemittanceStatus, VarianceType } from "../common/remittanceV2Constants";
import Currency from "../common/currency";
import { getRemittanceList, submitRemittanceV2Request, verifyRemittanceV2Request } from "../remittance/network/RemittanceAPI";
import AlertModal from "./alertModal";
import SectionDetails from "./deposit/sectionDetails";
import VarianceCard from "../common/varianceCard";
import { StringConstants } from "../common/constants";
import Tag from "@amzn/meridian/tag";

const SubmitRemittanceV2Modal = (props) => {
    const [isLoading, setIsLoading] = useState(true);
    const [alertModal, setAlertModal] = useState({});
    const [showSubmitModal, setShowSubmitModal] = useState();
    const [errorMessage, setErrorMessage] = useState();
    const [remittanceCode, setRemittanceCode] = useState();
    const [confirmRemittanceCode, setConfirmRemittanceCode] = useState();
    const [inputError, setInputError] = useState();
    const [cashCollectedByCCA, setCashCollectedByCCA] = useState({});
    const [actualAmount, setActualAmount] = useState({});
    const [totalVarianceList, setTotalVarianceList] = useState([]);
    const [uniqueBosonVariances, setUniqueBosonVariances] = useState([]);
    const [uniqueExternalVendorVariances, setUniqueExternalVendorVarance] = useState([]);
    const [transactionId, setTransactionId] = useState();

    useEffect(() => {
        getRemittanceList(getRemittanceRequestParams())
            .then((response) => {
                if (response.status === 200) {
                    const {data: { remittanceList }} = response;
                    processRemittanceList(remittanceList);
                } else {
                    setAlertModal({
                        show: true,
                        type: "error",
                        title: "Error",
                        message: "Failed to fetch bank deposits",
                        onClose: reload
                    });
                }
                setIsLoading(false);
            })
            .catch(() => {
                setAlertModal({
                    show: true,
                    type: "error",
                    title: "Error",
                    message: "Unexpected error occurred",
                    onClose: reload
                });
                setIsLoading(false);
            });
    }, []);

    const reload = () => {
        window.location.reload();
    };

    const clearInputErrorMessage = () => {
        setInputError();
    };

    /**
     * Validates remittance code and confirm remittance code
     * @returns boolean
     */
    const isValidInput = () => {
        if (!remittanceCode || !commonUtils.isValidRemittanceCode(remittanceCode, props.currencyUnit)) {
            setInputError("Enter a valid remittance code");
        } else if (!confirmRemittanceCode || confirmRemittanceCode !== remittanceCode) {
            setInputError("'Remittance' and 'Confirm Remittance' code does not match");
        } else {
            return true;
        }
        return false;
    };

    /**
     * This method will call submitRemittanceV2Request API.
     */
    const submitRemittance = () => {
        setErrorMessage();
        if (isValidInput()) {
            setIsLoading(true);
            submitRemittanceV2Request({
                stationCode: getStationCode(),
                remittanceCode: remittanceCode,
                cashCollected: cashCollectedByCCA.value,
                varianceList: totalVarianceList,
                transactionId: transactionId
            })
                .then((response) => {
                    if (response.status === 200) {
                        const { data } = response;
                        if (data.responseCode === StringConstants.SUCCESS) {
                            setAlertModal({
                                show: true,
                                type: "success",
                                title: "Success",
                                message:"Remittance code successfully submitted",
                                onClose: reload,
                            });
                        } else {
                            if (data.errorCode === ErrorCodes.INVALID_REMITTANCE_CODE) {
                                setErrorMessage("Error: " + getReadableLabel(data.errorCode));
                            } else {
                                setAlertModal({
                                    show: true,
                                    type: "error",
                                    title: "Error",
                                    message: getReadableLabel(data.errorCode),
                                    onClose: reload,
                                });
                            }
                            
                        }
                    } else {
                        setErrorMessage("Failed to submit remittance code");
                    }
                    setIsLoading(false);
                })
                .catch(() => {
                    setErrorMessage("Unexpected error occurred");
                    setIsLoading(false);
                });
        }
    };

    /**
     * Process the remittance list to show the modal content
     * @param {List} remittanceList 
     */
    const processRemittanceList = (remittanceList) => {
        let totalVariances = [],
            actualAmountValue = 0,
            excessVarianceValue = 0,
            shortVarianceValue = 0,
            uniqueBosonVariances = [],
            uniqueExternalVendorVariances = [];
        const variancesByUniqueReasonCodeMap = {}, variancesByUniqueReasonCodeList = [];
        //filtering remittance with status PENDING_SUBMISSION
        const filteredRemittanceList = remittanceList.filter(remittance => remittance.status === RemittanceStatus.PENDING_SUBMISSION);

        //Getting transaction Id from stationVariance.
        if (filteredRemittanceList.length > 0 && filteredRemittanceList[0].stationVariance) {
            setTransactionId(filteredRemittanceList[0].stationVariance.transactionId);
        }
        filteredRemittanceList.forEach((remittance) => {
            actualAmountValue += remittance.actualAmount.value;
            if (remittance.stationVariance) {
                // Cash collected by CCA = actualAmount + excessVarianceValue - shortVarianceValue
                remittance.stationVariance.stationVarianceList.forEach(variance => {
                    if (variance.type === VarianceType.SHORT) {
                        shortVarianceValue += variance.amount.value;
                    }
                    if (variance.type === VarianceType.EXCESS) {
                        excessVarianceValue += variance.amount.value;
                    }
                });
                // deep copy of remittance.stationVariance
                totalVariances.push(...JSON.parse(JSON.stringify(remittance.stationVariance.stationVarianceList)));

                //summing up variance together having the same variance reason
                remittance.stationVariance.stationVarianceList.forEach(variance => { insertVarianceByUniqueReasonCode(variancesByUniqueReasonCodeMap, variance) });
            }
        });

        //map to array conversion i.e. {key1: data1, key2: data2} to [data1, data2]
        Object.entries(variancesByUniqueReasonCodeMap).forEach(([, data]) => { variancesByUniqueReasonCodeList.push(data) });

        uniqueBosonVariances = variancesByUniqueReasonCodeList.filter(filterBosonVariance);
        uniqueExternalVendorVariances = variancesByUniqueReasonCodeList.filter(variance => !filterBosonVariance(variance));

        setUniqueBosonVariances(groupBy(uniqueBosonVariances, 3));
        setUniqueExternalVendorVarance(groupBy(uniqueExternalVendorVariances, 3));

        setActualAmount({ value: actualAmountValue, unit: props.currencyUnit });
        setCashCollectedByCCA({
            value: actualAmountValue + excessVarianceValue - shortVarianceValue,
            unit: props.currencyUnit,
        });
        setTotalVarianceList(totalVariances);
        setShowSubmitModal(true);
    };

    const resetRemittance = () => {
        setIsLoading(true);
        verifyRemittanceV2Request({
            stationCode: getStationCode(),
            verified: false,
            transactionId: transactionId
        }).then(response => {
            if (response.status === 200) {
                const { data } = response;
                if (data.responseCode === StringConstants.SUCCESS) {
                    setAlertModal({
                        show: true,
                        type: "success",
                        title: "Success",
                        message: "Successfully reset the remittance",
                        onClose: reload
                    });
                } else {
                    setErrorMessage("Error: " + getReadableLabel(data.errorCode));
                }
            } else {
                setErrorMessage("Unable to process the remittance");
            }
            setIsLoading(false);
        }).catch(() => {
            setErrorMessage("Unexpected error occurred");
            setIsLoading(false);
        })
    }

    return (
        <React.Fragment>
            {isLoading && <AppLoader />}
            <Modal
                title="Submit Remittance"
                open={showSubmitModal}
                scrollContainer="viewport"
            >
                <Column spacing="medium">
                    <Column spacing="small">
                    {transactionId ? <Tag>Transaction ID: {transactionId}</Tag> : <Tag type="warning">Transaction Id is not generated</Tag>}
                        <SectionDetails
                            title="Overview"
                            description="Amount that should ideally be submitted and actual amount getting submitted."
                        />
                        <Row
                            spacing="large"
                            widths={270}
                            alignmentHorizontal="center"
                        >
                            <Card>
                                <CardHeader>
                                    <Heading>Actual Amount</Heading>
                                </CardHeader>
                                <Currency amount={actualAmount} />
                            </Card>
                            <Card>
                                <CardHeader>
                                    <Heading>Submitted Amount</Heading>
                                </CardHeader>
                                <Currency amount={cashCollectedByCCA} />
                            </Card>
                        </Row>
                    </Column>

                    <Column spacing="small">
                        <SectionDetails
                            title="Station Variance Summary"
                            description="Variances on the station side"
                        />
                        <Column>
                            {uniqueBosonVariances.length > 0 ? (
                                uniqueBosonVariances.map((varianceGroup) => (
                                    <Row>
                                        {varianceGroup.map((variance) => (
                                            <VarianceCard
                                                width={180}
                                                variance={variance}
                                            />
                                        ))}
                                    </Row>
                                ))
                            ) : (
                                <Alert>
                                    There is no variance at the station side
                                </Alert>
                            )}
                        </Column>
                    </Column>

                    <Column spacing="small">
                        <SectionDetails
                            title="CCA Variance Summary"
                            description="Variances entered by the CCA"
                        />
                        <Column>
                            {uniqueExternalVendorVariances.length > 0 ? (
                                uniqueExternalVendorVariances.map((varianceGroup) => (
                                    <Row>
                                        {varianceGroup.map((variance) => (
                                            <VarianceCard
                                                width={180}
                                                variance={variance}
                                            />
                                        ))}
                                    </Row>
                                ))
                            ) : (
                                <Alert>
                                    There is no variance at the vendor side
                                </Alert>
                            )}
                        </Column>
                    </Column>

                    <Column spacing="small">
                        <SectionDetails
                            title="Remittance Code"
                            description="Enter remittance code generated by CCA"
                        />
                        <Input
                            label="Remittance Code"
                            value={remittanceCode}
                            onChange={setRemittanceCode}
                            onFocus={clearInputErrorMessage}
                        />
                        <Input
                            label="Confirm Remittance Code"
                            value={confirmRemittanceCode}
                            onChange={setConfirmRemittanceCode}
                            onFocus={clearInputErrorMessage}
                        />
                        {inputError && <Alert type="error">{inputError}</Alert>}
                    </Column>
                    {errorMessage && <Alert type="error">{errorMessage}</Alert>}
                </Column>

                <ModalFooter>
                <Row widths={["grid-8", "grid-4"]} >
                        <Column>
                           <Row alignmentHorizontal="left">
                            <Button
                                    type="tertiary"
                                    size="large"
                                    disabled={isLoading}
                                    onClick={reload}
                                >
                                    Close
                                </Button>
                           </Row>
                        </Column>
                        <Column>
                            <Row alignmentHorizontal="right" widths="fit">
                                <Button type="secondary" size="large" disabled={isLoading} onClick={resetRemittance}>
                                    Reject
                                </Button>
                                <Button
                                    type="primary"
                                    size="large"
                                    disabled={isLoading}
                                    onClick={submitRemittance}
                                >
                                    Submit
                                </Button>
                            </Row>
                        </Column>
                    </Row>

                    
                    
                </ModalFooter>
            </Modal>
            <AlertModal {...alertModal} />
        </React.Fragment>
    );
};

export default SubmitRemittanceV2Modal;



// WEBPACK FOOTER //
// ./src/component/modal/submitRemittanceV2Modal.js